import { z } from 'zod';

export type NumberBounds = { min: number; max: number };

/** Clamp a numeric value into bounds; pass null/undefined through untouched. */
export const clampToBounds = <T extends number | null | undefined>(value: T, { min, max }: NumberBounds): T =>
    typeof value === 'number' ? (Math.min(max, Math.max(min, value)) as T) : value;

/** A breakpoint — the one spelling every entity, feature and host reads. */
export type Device = 'desktop' | 'tablet' | 'mobile';

export const DEVICES = ['desktop', 'tablet', 'mobile'] as const satisfies readonly Device[];

/** Per-device suffix on CSS variable names; desktop carries none. */
export const DEVICE_SUFFIX: Record<Device, string> = { desktop: '', tablet: '-tablet', mobile: '-mobile' };

/** The four sides of a box — padding, contour. */
export type Side = 'top' | 'right' | 'bottom' | 'left';

export const SIDES = ['top', 'right', 'bottom', 'left'] as const satisfies readonly Side[];

/** The four corners of a box — radius. */
export type Corner = 'topLeft' | 'topRight' | 'bottomRight' | 'bottomLeft';

export const CORNERS = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'] as const satisfies readonly Corner[];

/** A value per breakpoint, loosely: what a reader gets handed, stored or hand-built. */
export type Responsive<T> = { desktop?: T; tablet?: T; mobile?: T };

/**
 * The value at a breakpoint. Every responsive leaf carries all three after the
 * migration, but the schema keeps `tablet` and `mobile` optional — so a reader
 * falls back to desktop rather than to an undefined.
 */
export const atDevice = <T>(responsive: Responsive<T> | null | undefined, device: Device = 'desktop'): T | undefined =>
    responsive ? (responsive[device] ?? responsive.desktop) : undefined;

/**
 * The one responsive shape in the package. `mobile` and `tablet` are optional:
 * an entity that never had a per-device override simply omits the key, and the
 * entities that predate a breakpoint keep parsing.
 *
 * Deliberately one dialect rather than a per-entity wrapper — a second spelling
 * of the same idea is what let a section hand a column a vocabulary it could not
 * read (see `mixins/alignment`).
 */
export const responsiveSchema = <T extends z.ZodTypeAny>(value: T) =>
    z.object({
        desktop: value,
        tablet: value.optional(),
        mobile: value.optional(),
    });

export const responsiveStringSchema = responsiveSchema(z.string());
export const responsiveNullableStringSchema = responsiveSchema(z.string().nullable());

/** Legacy wrote an empty string for an unset CSS keyword (`background-size` and the like); v0 writes `null`. */
export const cssKeywordOrNull = (value: unknown): string | null =>
    value == null || value === '' ? null : String(value);

export const responsiveBooleanSchema = responsiveSchema(z.boolean());
export const responsiveNumberSchema = responsiveSchema(z.number());

/**
 * An object that may be stored as `[]`. landings-service rewrites some page structures through PHP
 * arrays (Copy to page, a pipeline swap), which writes an empty object that way — and an empty bag
 * that fails here takes its whole block off the editor's canvas. Typed as the schema it wraps: zod
 * infers a preprocessed key as optional, which it is not.
 */
export const storedObjectSchema = <T extends z.ZodTypeAny>(schema: T): T =>
    z.preprocess((value) => (Array.isArray(value) && value.length === 0 ? {} : value), schema) as unknown as T;

/**
 * The child list of a structure entity, whichever key it is under.
 *
 * A migrated entity keeps its rows on `children`. An entity whose migration
 * failed is handed back raw, and a raw entity still has the persisted key —
 * `sections` on a block, `columns` on a row, `widgets` on a column. Reading only
 * `children` there either throws on the following `.map` or silently empties the
 * entity, and in the editor the emptied version is what gets saved back.
 */
export const entityChildren = <T = unknown>(entity: unknown, legacyKey?: 'sections' | 'columns' | 'widgets'): T[] => {
    const bag = (entity ?? {}) as Record<string, unknown>;

    if (Array.isArray(bag['children'])) {
        return bag['children'] as T[];
    }

    if (legacyKey && Array.isArray(bag[legacyKey])) {
        return bag[legacyKey] as T[];
    }

    return [];
};

/**
 * When the entity was created, in ms since the epoch — or `null` when nobody knows, which is every
 * entity that already existed when the field was added: a legacy row migrated on read, or a v0 one
 * saved before this. Defaulted rather than required, so those keep parsing.
 */
export const entityCreatedAtSchema = z.number().int().nonnegative().nullable().default(null);

/** `meta` for the entities that had none of their own until `createdAt` (column, section, grid, tab). */
export const entityMetaSchema = z.object({ createdAt: entityCreatedAtSchema }).default({ createdAt: null });

export type EntityMeta = z.infer<typeof entityMetaSchema>;

/** A fresh entity's `meta` — call it per entity, never share one object: the time is the point. */
export const createEntityMeta = (): EntityMeta => ({ createdAt: Date.now() });
